import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import {
  FileText, Plus, Loader2, Send, CheckCircle, Clock, AlertCircle, Eye, BarChart3, RefreshCw, Unlock
} from 'lucide-react';

const ESTADO_BADGES = {
  borrador: { label: 'Borrador', cls: 'bg-gray-100 text-gray-700' },
  enviada: { label: 'Enviada', cls: 'bg-blue-100 text-blue-700' },
  parcial: { label: 'Parcial', cls: 'bg-amber-100 text-amber-700' },
  completa: { label: 'Completa', cls: 'bg-green-100 text-green-700' },
  cerrada: { label: 'Cerrada', cls: 'bg-gray-200 text-gray-500' },
};

export function CotizacionesList() {
  const navigate = useNavigate();
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [sendingId, setSendingId] = useState(null);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  // Iter 300: reabrir SC cerrada + ver comparativo PDF de cerradas
  const [reabriendoId, setReabriendoId] = useState(null);
  const [compPdfLoadingId, setCompPdfLoadingId] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await api.get('/compras/cotizaciones?limit=50');
      setSolicitudes(res.data?.solicitudes || []);
      setTotal(res.data?.total || 0);
    } catch { toast.error('Error al cargar cotizaciones'); }
    setLoading(false);
  };

  useEffect(() => { fetchData(); }, []);

  const handleEnviar = async (scId, numero) => {
    setSendingId(scId);
    try {
      const res = await api.post(`/compras/cotizaciones/${scId}/enviar`);
      const enviados = res.data?.enviados || 0;
      const errores = res.data?.errores || [];
      if (enviados > 0) {
        toast.success(`${numero}: Enviada a ${enviados} proveedor(es)`);
      }
      errores.forEach(e => toast.error(e));
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al enviar');
    }
    setSendingId(null);
  };

  const handleVerPdf = async (scId) => {
    setPdfLoadingId(scId);
    try {
      const res = await api.get(`/compras/cotizaciones/${scId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (err) {
      toast.error('Error al cargar PDF');

    }
    setPdfLoadingId(null);
  };

  const formatDate = (d) => {
    if (!d) return '-';
    try {
      return new Date(d).toLocaleDateString('es-CL', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch { return d; }
  };

  // Iter 300: reabrir una SC cerrada (backfill de OCs por ítem en el backend)
  const handleReabrir = async (scId, numero) => {
    if (!window.confirm(`¿Reabrir la solicitud ${numero}?\n\nLos ítems que ya tienen OC quedarán marcados con su N° de OC y los pendientes podrán recibir OC desde el cuadro comparativo.`)) return;
    setReabriendoId(scId);
    try {
      const res = await api.post(`/compras/cotizaciones/${scId}/reabrir-comparativo`);
      toast.success(`${numero} reabierta — ${res.data?.items_pendientes ?? 0} ítem(s) pendientes de OC`);
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al reabrir la solicitud');
    }
    setReabriendoId(null);
  };

  // Iter 300: comparativo PDF de una SC cerrada (incluye observación de cierre)
  const handleComparativoPdf = async (scId) => {
    setCompPdfLoadingId(scId);
    try {
      const res = await api.get(`/compras/cotizaciones/${scId}/comparativo/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch {
      toast.error('Error al cargar el comparativo');
    }
    setCompPdfLoadingId(null);
  };

  return (
    <div className="p-4 md:p-6 max-w-7xl mx-auto" data-testid="cotizaciones-list-page">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-[#003366] rounded-lg flex items-center justify-center">
            <FileText className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900">Solicitudes de Cotizacion</h1>
            <p className="text-sm text-muted-foreground">{total} solicitud(es) en total</p>
          </div>
        </div>
        <Button onClick={() => navigate('/compras/solicitar-cotizacion')} className="bg-[#003366] gap-1.5"
          data-testid="nueva-solicitud-btn">
          <Plus className="w-4 h-4" /> Nueva Solicitud
        </Button>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-[#003366]" /></div>
      ) : solicitudes.length === 0 ? (
        <div className="border-2 border-dashed rounded-lg p-12 text-center text-muted-foreground">
          <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" />
          <p>No hay solicitudes de cotizacion aun.</p>
          <Button onClick={() => navigate('/compras/solicitar-cotizacion')} className="mt-4 bg-[#003366]">
            Crear Primera Solicitud
          </Button>
        </div>
      ) : (
        <div className="lg:flex lg:gap-4 lg:items-start">
          <div className="flex-1 border rounded-lg overflow-x-auto bg-white">
          <table className="w-full text-sm">
            <thead className="bg-[#003366] text-white text-xs">
              <tr>
                <th className="px-4 py-2 text-left">Numero</th>
                <th className="px-4 py-2 text-left">Fecha</th>
                <th className="px-4 py-2 text-center">Items</th>
                <th className="px-4 py-2 text-center">Proveedores</th>
                <th className="px-4 py-2 text-center">Respondidas</th>
                <th className="px-4 py-2 text-center">Estado</th>
                <th className="px-4 py-2 text-left">Creado por</th>
                <th className="px-4 py-2 text-center">Vencimiento</th>
                <th className="px-4 py-2 text-center">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {solicitudes.map((s, i) => {
                const badge = ESTADO_BADGES[s.estado] || ESTADO_BADGES.borrador;
                const respondidas = (s.proveedores || []).filter(p => p.estado === 'respondida').length;
                return (
                  <tr key={s.id || i} className={`hover:bg-gray-50 ${i % 2 === 0 ? 'bg-gray-50/50' : ''}`}
                    data-testid={`sc-row-${s.numero}`}>
                    <td className="px-4 py-2 font-mono font-semibold text-[#003366]">{s.numero}</td>
                    <td className="px-4 py-2 text-muted-foreground">{formatDate(s.fecha_creacion)}</td>
                    <td className="px-4 py-2 text-center">{(s.items || []).length}</td>
                    <td className="px-4 py-2 text-center">{(s.proveedores || []).length}</td>
                    <td className="px-4 py-2 text-center">
                      <span className={respondidas > 0 ? 'text-green-600 font-semibold' : 'text-muted-foreground'}>
                        {respondidas}/{(s.proveedores || []).length}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-center">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${badge.cls}`}>{badge.label}</span>
                    </td>
                    <td className="px-4 py-2 text-xs text-muted-foreground">{s.creado_por_nombre || s.creado_por}</td>
                    <td className="px-4 py-2 text-center text-xs">{formatDate(s.fecha_vencimiento)}</td>
                    <td className="px-4 py-2 text-center">
                      <div className="flex justify-center gap-1">
                        {s.estado === 'borrador' ? (
                          <Button variant="ghost" size="sm" title="Enviar a Proveedores"
                            disabled={sendingId === s.id}
                            onClick={() => handleEnviar(s.id, s.numero)}
                            data-testid={`enviar-sc-${s.numero}`}>
                            {sendingId === s.id
                              ? <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                              : <Send className="w-4 h-4 text-blue-600" />}
                          </Button>
                        ) : (
                          <Button variant="ghost" size="sm" title="Reenviar Correos"
                            disabled={sendingId === s.id}
                            onClick={() => handleEnviar(s.id, s.numero)}
                            data-testid={`reenviar-sc-${s.numero}`}>
                            {sendingId === s.id
                              ? <Loader2 className="w-4 h-4 animate-spin text-green-600" />
                              : <RefreshCw className="w-4 h-4 text-green-600" />}
                          </Button>
                        )}
                        <Button variant="ghost" size="sm" title="Ver PDF"
                          disabled={pdfLoadingId === s.id}
                          onClick={() => handleVerPdf(s.id)}
                          data-testid={`pdf-sc-${s.numero}`}>
                          {pdfLoadingId === s.id
                            ? <Loader2 className="w-4 h-4 animate-spin text-blue-600" />
                            : <Eye className="w-4 h-4 text-blue-600" />}
                        </Button>
                        {['enviada', 'parcial', 'completa'].includes(s.estado) && (
                          <Button variant="ghost" size="sm" title="Cuadro Comparativo"
                            onClick={() => navigate(`/compras/cotizaciones/${s.id}/comparativo`)}
                            data-testid={`comparativo-sc-${s.numero}`}>
                            <BarChart3 className="w-4 h-4 text-emerald-600" />
                          </Button>
                        )}
                        {s.estado === 'cerrada' && (
                          <>
                            <Button variant="ghost" size="sm" title="Comparativo PDF (con observación de cierre)"
                              disabled={compPdfLoadingId === s.id}
                              onClick={() => handleComparativoPdf(s.id)}
                              data-testid={`comparativo-pdf-sc-${s.numero}`}>
                              {compPdfLoadingId === s.id
                                ? <Loader2 className="w-4 h-4 animate-spin text-gray-500" />
                                : <BarChart3 className="w-4 h-4 text-gray-500" />}
                            </Button>
                            <Button variant="ghost" size="sm" title="Reabrir solicitud"
                              disabled={reabriendoId === s.id}
                              onClick={() => handleReabrir(s.id, s.numero)}
                              data-testid={`reabrir-sc-${s.numero}`}>
                              {reabriendoId === s.id
                                ? <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                                : <Unlock className="w-4 h-4 text-amber-600" />}
                            </Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>

          {/* Iter 301: leyenda lateral de estados */}
          <aside className="mt-4 lg:mt-0 lg:w-56 shrink-0 border rounded-lg bg-white p-4" data-testid="leyenda-estados">
            <p className="text-xs font-bold text-gray-700 mb-3 uppercase tracking-wide">Leyenda de estados</p>
            <div className="space-y-3">
              {[
                { key: 'borrador', desc: 'Creada, aún no se envía a los proveedores.' },
                { key: 'enviada', desc: 'Enviada a proveedores; ninguno ha respondido aún.' },
                { key: 'parcial', desc: 'Algunos proveedores han respondido; se puede trabajar el comparativo.' },
                { key: 'completa', desc: 'Todos los proveedores respondieron; lista para comparar y generar OC.' },
                { key: 'cerrada', desc: 'Comparativo cerrado (con o sin OC generadas). Se puede reabrir con el candado.' },
              ].map(({ key, desc }) => (
                <div key={key} className="flex items-start gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium shrink-0 ${ESTADO_BADGES[key].cls}`}>
                    {ESTADO_BADGES[key].label}
                  </span>
                  <p className="text-[11px] leading-snug text-gray-500">{desc}</p>
                </div>
              ))}
            </div>
          </aside>
        </div>
      )}
    </div>
  );
}
